// apps/ecommerce/src/components/ai-chat/chat-message.tsx
import { memo, useState, useEffect } from "react";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";
import { StreamingCursor } from "./streaming-cursor";
import { getArtifactBlob, type Artifact, type ToolCall } from "~/lib/network/shopify-chat";
import "./tool-status.css";

interface ChatMessageProps {
  role: "user" | "assistant";
  content: string;
  isStreaming?: boolean;
  tools?: ToolCall[];
  connectionId?: string;
  artifacts?: Artifact[];
}

// Tool status line component - shows shimmer when running, checkmark when done
function ToolStatusLine({ tool }: { tool: ToolCall }) {
  const isRunning = tool.status === "running";
  return (
    <div className="flex items-center gap-2 text-sm my-2">
      {isRunning ? (
        <span className="tool-shimmer">{tool.display_name}...</span>
      ) : (
        <>
          <svg className="w-4 h-4 text-success" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          <span className="text-base-content/70">{tool.display_name}</span>
        </>
      )}
    </div>
  );
}

// Artifact link component - renders as a button/card
function ArtifactLink({
  href,
  children,
  connectionId,
}: {
  href: string;
  children: React.ReactNode;
  connectionId: string;
}) {
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Extract filename from /artifact/filename
  const filename = href.replace("/artifact/", "");
  const isHtml = filename.endsWith(".html");

  // Cleanup blob URL when component unmounts or modal closes
  useEffect(() => {
    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [blobUrl]);

  const handleOpenPreview = async () => {
    if (isHtml) {
      setIsPreviewOpen(true);
      setIsLoading(true);
      setError(null);

      try {
        const blob = await getArtifactBlob(connectionId, filename);
        const url = URL.createObjectURL(blob);
        setBlobUrl(url);
      } catch (err) {
        console.error("Failed to load artifact:", err);
        setError("Failed to load artifact");
      } finally {
        setIsLoading(false);
      }
    } else {
      // For non-HTML files (CSV, Excel), download directly
      try {
        const blob = await getArtifactBlob(connectionId, filename);
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } catch (err) {
        console.error("Failed to download artifact:", err);
      }
    }
  };

  const handleClosePreview = () => {
    setIsPreviewOpen(false);
    if (blobUrl) {
      URL.revokeObjectURL(blobUrl);
      setBlobUrl(null);
    }
  };

  return (
    <>
      <button
        onClick={handleOpenPreview}
        className="inline-flex items-center gap-1 text-primary hover:text-primary-focus underline underline-offset-2 transition-colors cursor-pointer"
      >
        {isHtml ? (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
            />
          </svg>
        ) : (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
            />
          </svg>
        )}
        {children}
      </button>

      {/* Preview modal for HTML artifacts (charts) */}
      {isPreviewOpen && isHtml && (
        <dialog className="modal modal-open" onClick={handleClosePreview}>
          <div
            className="modal-box w-11/12 max-w-4xl h-[80vh] p-0 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b border-base-300">
              <h3 className="font-semibold">{children}</h3>
              <button className="btn btn-sm btn-ghost" onClick={handleClosePreview}>
                ✕
              </button>
            </div>
            {isLoading ? (
              <div className="flex items-center justify-center h-[calc(100%-60px)]">
                <span className="loading loading-spinner loading-lg"></span>
              </div>
            ) : error ? (
              <div className="flex items-center justify-center h-[calc(100%-60px)] text-error">
                {error}
              </div>
            ) : blobUrl ? (
              <iframe
                src={blobUrl}
                className="w-full h-[calc(100%-60px)] border-0"
                title={String(children)}
              />
            ) : null}
          </div>
        </dialog>
      )}
    </>
  );
}

// Markdown component config for artifact links
function createMarkdownComponents(connectionId?: string) {
  return {
    // Custom link renderer for artifact links
    a: ({ href, children, ...props }: { href?: string; children?: React.ReactNode }) => {
      if (href?.startsWith("/artifact/") && connectionId) {
        return (
          <ArtifactLink href={href} connectionId={connectionId}>
            {children}
          </ArtifactLink>
        );
      }
      // Regular links
      return (
        <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
          {children}
        </a>
      );
    },
    // Style tables nicely
    table: ({ children }: { children?: React.ReactNode }) => (
      <div className="overflow-x-auto my-2">
        <table className="table table-xs">{children}</table>
      </div>
    ),
  };
}

// Render content with tools interspersed at their positions
function ContentWithTools({
  content,
  tools,
  connectionId,
  isStreaming,
}: {
  content: string;
  tools: ToolCall[];
  connectionId?: string;
  isStreaming?: boolean;
}) {
  const components = createMarkdownComponents(connectionId);
  const remarkPlugins = [remarkGfm, remarkBreaks];

  // Sort tools by position
  const sortedTools = [...tools].sort((a, b) => a.content_position - b.content_position);

  // Build segments: content chunks interspersed with tools
  const segments: React.ReactNode[] = [];
  let lastPos = 0;

  sortedTools.forEach((tool, index) => {
    // Content before this tool
    if (tool.content_position > lastPos) {
      const chunk = content.slice(lastPos, tool.content_position);
      if (chunk) {
        segments.push(
          <Markdown key={`content-${index}`} remarkPlugins={remarkPlugins} components={components}>
            {chunk}
          </Markdown>
        );
      }
    }
    // Tool status
    segments.push(<ToolStatusLine key={`tool-${index}`} tool={tool} />);
    lastPos = tool.content_position;
  });

  // Remaining content after last tool
  const remaining = content.slice(lastPos);
  if (remaining) {
    segments.push(
      <Markdown key="content-final" remarkPlugins={remarkPlugins} components={components}>
        {remaining}
      </Markdown>
    );
  }

  // Streaming cursor (only if no tool is currently running)
  const hasRunningTool = tools.some((t) => t.status === "running");
  if (isStreaming && !hasRunningTool) {
    segments.push(<StreamingCursor key="cursor" />);
  }

  return <>{segments}</>;
}

export const ChatMessage = memo(function ChatMessage({
  role,
  content,
  isStreaming,
  tools = [],
  connectionId,
}: ChatMessageProps) {
  const isUser = role === "user";

  return (
    <div className={`chat ${isUser ? "chat-end" : "chat-start"}`}>
      <div className="chat-header text-xs opacity-50">{isUser ? "You" : "Gamgee"}</div>
      <div className="chat-bubble whitespace-pre-wrap max-w-[85%]">
        {isUser ? (
          // User messages: plain text, slightly smaller
          <span className="text-sm">
            {content}
            {isStreaming && <StreamingCursor />}
          </span>
        ) : (
          // Assistant messages: render markdown with tools inline
          <div className="prose prose-sm prose-invert max-w-none [&>*:first-child]:mt-0 [&>*:last-child]:mb-0 prose-p:my-0.5 prose-ul:my-0.5 prose-ol:my-0.5 prose-li:my-0 prose-headings:mb-0.5 prose-headings:mt-2 prose-hr:my-2 prose-table:my-1">
            <ContentWithTools
              content={content}
              tools={tools}
              connectionId={connectionId}
              isStreaming={isStreaming}
            />
          </div>
        )}
      </div>
    </div>
  );
});
